import {Fragment} from 'react';

import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Container, media, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {LinkBasic} from 'UI/components/action/LinkBasic';
import {H1, Label, P} from 'UI/components/layout/Text';
import {FooterLink} from 'UI/components/modules/Footer/FooterLink';

import {useFooter} from './useFooter';
import {LogoMnetInv, LogoSwmInv, LogoTelekomInv} from 'Images/icons';

import type {Footer as FooterType} from 'types/payload-types';

/**
 * The props for the `Footer` component, which renders the footer section of the site.
 * This interface defines the data required to build the footer, including configuration
 * for text, links, logos, and colors. It also supports an optional `testId` that allows
 * the component to be uniquely targeted in automated testing environments.
 */
interface ComponentProps {
    /**
     * The footer configuration data containing text, links, colors, and logos.
     * This object is passed into the `useFooter` hook to extract all rendering details.
     */
    footerData: FooterType;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Footer'
     */
    testId?: string;
}

/**
 * A footer component that renders legal information, partner logos, and navigation links.
 * This component uses the `useFooter` hook to retrieve all necessary data from `footerData`,
 * including colors, copyright text, links, and textual elements. It displays an overline,
 * headline, partner logos, and a set of footer navigation links. Between links, separators
 * are rendered to improve readability. At the bottom, the copyright notice is displayed
 * with the current year.
 * The component is wrapped in MobX's `observer` to reactively update when observable
 * data changes. It also applies a `data-cy` attribute for reliable end-to-end test targeting.
 *
 * @param props            The component props.
 * @param props.footerData The footer configuration data including text, colors, links, and logos.
 * @param props.testId     Optional test ID used for automation selectors. Defaults to `'Footer'`.
 * @returns A JSX element rendering the footer layout.
 *
 * @example
 * ```tsx
 * <Footer footerData={footerConfig} />
 * ```
 */
const Footer = observer(({footerData, testId = 'Footer'}: ComponentProps) => {
    const {
        colors,
        copyright,
        footerLinks,
        headline,
        mnetHref,
        mnetLabel,
        mnetRel,
        mnetTarget,
        overline,
        swmHref,
        swmLabel,
        swmRel,
        swmTarget,
        telHref,
        telLabel,
        telRel,
        telTarget,
        year
    } = useFooter(footerData);

    return (
        <Wrapper data-cy={testId}>
            <BackgroundWrapper>
                <FooterContent>
                    <FooterTopHalf>
                        <TextWrapper>
                            {overline && <Label><Color $color={colors.textSecondaryInverted}>{overline}</Color></Label>}
                            <Spacer y={6} isNotStretching />
                            {headline && <H1 as="p"><Color $color={colors.textPrimaryInverted}>{headline}</Color></H1>}
                        </TextWrapper>
                        <LogoWrapper>
                            <LinkBasic aria-label={swmLabel} href={swmHref!} rel={swmRel} target={swmTarget}>
                                <LogoSwmInv />
                            </LinkBasic>
                            <LinkBasic aria-label={mnetLabel} href={mnetHref!} rel={mnetRel} target={mnetTarget}>
                                <LogoMnetInv />
                            </LinkBasic>
                            <LinkBasic aria-label={telLabel} href={telHref!} rel={telRel} target={telTarget}>
                                <LogoTelekomInv />
                            </LinkBasic>
                        </LogoWrapper>
                    </FooterTopHalf>
                    <FooterBottomHalf>
                        <LinkWrapper>
                            {footerLinks.map((link, index) => {
                                const isLast = index === footerLinks.length - 1;

                                return (
                                    <Fragment key={link.id}>
                                        <FooterLink link={link.footerLink} />
                                        {!isLast && (
                                            <P $size="extra-small" as="span">
                                                <Color $color={colors.textSecondaryInverted}>•</Color>
                                            </P>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </LinkWrapper>
                        <CopyrightWrapper>
                            <P $size="extra-small">
                                <Color $color={colors.textSecondaryInverted}>
                                    {copyright && <>© {year} {copyright}</>}
                                </Color>
                            </P>
                        </CopyrightWrapper>
                    </FooterBottomHalf>
                </FooterContent>
            </BackgroundWrapper>
        </Wrapper>
    );
});

Footer.displayName = 'Footer';

export {Footer};

const Wrapper = styled.footer`
    flex: 0 0 58.8rem;
    padding-block-start: ${spacing(30)};

    ${media('md')} {
        padding-block-start: ${spacing(36)};
    }
`;

const BackgroundWrapper = styled.div`
    background-color: ${({theme}) => theme.colors.neutral800};
    height: 100%;
    padding-block: ${spacing(20)} ${spacing(16)};
`;

const FooterContent = styled(Container)`
    display: flex;
    flex-direction: column;
    gap: ${spacing(16)};

    ${media('md')} {
        gap: ${spacing(20)};
    }
`;

const FooterTopHalf = styled.div`
    align-items: start;
    display: flex;
    flex-direction: column;
    gap: ${spacing(12)};

    ${media('md')} {
        align-items: center;
        flex-direction: row;
        gap: 0;
        justify-content: space-between;
    }
`;

const TextWrapper = styled.div``;

const LogoWrapper = styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing(9)};
    justify-content: center;

    ${media('sm')} {
        gap: ${spacing(12)};
    }

    ${media('lg')} {
        gap: ${spacing(16)};
    }
`;

const FooterBottomHalf = styled.div`
    align-items: start;
    border-top: 1px solid ${({theme}) => theme.colors.neutral700};
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
    padding-block-start: ${spacing(12)};

    ${media('md')} {
        flex-direction: row;
        gap: 0;
        justify-content: space-between;
    }
`;

const LinkWrapper = styled.div`
    display: flex;
    gap: ${spacing(4)};
`;

const CopyrightWrapper = styled.div``;